/**
 * MovingHazard: a sensor rectangle that follows a polyline path and kills the
 * player on contact (label: 'hazard'). Combines `MovingPlatform`'s path math
 * with `Hazard`'s sensor + spike visual.
 *
 * Implementation notes
 *  - Body is `isStatic: true, isSensor: true, label: 'hazard'`. We teleport
 *    it each tick (same pattern as MovingPlatform) and synthesize a velocity
 *    so collisionStart still fires reliably for fast-moving ones.
 *  - The visual is centered around the body origin (Pixi local coords) so a
 *    single `g.x/g.y` update moves the whole spike strip with the body.
 */

import Matter from 'matter-js';
import { Graphics } from 'pixi.js';
import type { PathSpec } from '../Path.js';
import { pathPosition } from '../Path.js';

export interface MovingHazardOptions {
  x: number;
  y: number;
  w: number;
  h: number;
  path: PathSpec;
}

export class MovingHazard {
  readonly body: Matter.Body;
  readonly view: Graphics;
  readonly pathView: Graphics;
  private readonly path: PathSpec;
  private readonly halfW: number;
  private readonly halfH: number;
  private readonly originOffsetX: number;
  private readonly originOffsetY: number;
  private prevX: number;
  private prevY: number;

  constructor(opts: MovingHazardOptions) {
    this.path = opts.path;
    this.halfW = opts.w / 2;
    this.halfH = opts.h / 2;
    this.originOffsetX = opts.x + this.halfW - opts.path.points[0]!.x;
    this.originOffsetY = opts.y + this.halfH - opts.path.points[0]!.y;

    const start = pathPosition(opts.path, 0);
    const cx = start.x + this.originOffsetX;
    const cy = start.y + this.originOffsetY;

    this.body = Matter.Bodies.rectangle(cx, cy, opts.w, opts.h, {
      isStatic: true,
      isSensor: true,
      label: 'hazard',
    });
    this.prevX = cx;
    this.prevY = cy;

    // Spike strip in body-local coords (origin at body center).
    const g = new Graphics();
    const spikeW = 16;
    const count = Math.max(1, Math.floor(opts.w / spikeW));
    const actualW = opts.w / count;
    const left = -this.halfW;
    const top = -this.halfH;
    for (let i = 0; i < count; i++) {
      const x0 = left + i * actualW;
      g.moveTo(x0, top + opts.h)
        .lineTo(x0 + actualW / 2, top)
        .lineTo(x0 + actualW, top + opts.h)
        .closePath()
        .fill(0xc83a3a)
        .stroke({ color: 0x401010, width: 1.5 });
    }
    g.x = cx;
    g.y = cy;
    this.view = g;

    // Dotted breadcrumb of the path (matches MovingPlatform style, red tint).
    const pv = new Graphics();
    const pts = opts.path.points;
    if (pts.length >= 2) {
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1]!, b = pts[i]!;
        const dx = b.x - a.x, dy = b.y - a.y;
        const L = Math.hypot(dx, dy);
        if (L < 0.001) continue;
        const ux = dx / L, uy = dy / L;
        const period = 10;
        let t = 0;
        while (t < L) {
          const t2 = Math.min(t + 4, L);
          pv.moveTo(a.x + ux * t + this.originOffsetX, a.y + uy * t + this.originOffsetY)
            .lineTo(a.x + ux * t2 + this.originOffsetX, a.y + uy * t2 + this.originOffsetY)
            .stroke({ color: 0xff8888, width: 2, alpha: 0.35 });
          t += period;
        }
      }
      for (const p of pts) {
        pv.circle(p.x + this.originOffsetX, p.y + this.originOffsetY, 2.5)
          .fill({ color: 0xffbbbb, alpha: 0.45 });
      }
    }
    this.pathView = pv;
  }

  /** Advance the body to its position at level-time `tSec`. */
  step(tSec: number, dtSec: number): void {
    const p = pathPosition(this.path, tSec);
    const cx = p.x + this.originOffsetX;
    const cy = p.y + this.originOffsetY;
    Matter.Body.setPosition(this.body, { x: cx, y: cy });
    const vx = dtSec > 0 ? (cx - this.prevX) / dtSec : 0;
    const vy = dtSec > 0 ? (cy - this.prevY) / dtSec : 0;
    Matter.Body.setVelocity(this.body, { x: vx, y: vy });
    this.prevX = cx;
    this.prevY = cy;
    this.view.x = cx;
    this.view.y = cy;
  }
}
